import React from 'react';

interface AeoAnswerProps {
  question?: string;
  children: React.ReactNode;
  className?: string;
  /** If true, the section will be marked as an entity-answer for better bot discovery */
  isEntityAnswer?: boolean;
}

/**
 * AeoAnswer Component
 * 
 * Standardized component for capturing and displaying AI-optimized answers.
 * It uses the 'aeo-answer' ID which is currently picked up by our build-time 
 * and render-time parsing logic for structured data (Article and FAQ schemas).
 */
export const AeoAnswer: React.FC<AeoAnswerProps> = ({ 
  question, 
  children, 
  className = "",
  isEntityAnswer = true
}) => {
  return (
    <section 
      id="aeo-answer" 
      className={`relative p-8 glass rounded-3xl border border-primary/20 bg-primary/[0.02] overflow-hidden ${className}`}
      data-aeo-type={isEntityAnswer ? "entity-answer" : "info"}
    >
      {/* Visual background hint */}
      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-primary/5 rounded-full blur-2xl pointer-events-none" />
      
      {question && (
        <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-black">?</span>
          {question}
        </h2>
      )}
      
      <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground leading-relaxed">
        {children}
      </div>
      
      {/* Bot hidden hint */}
      <div className="hidden" aria-hidden="true" data-type="ai-grounding-point">
        Grounding data for {question || "this section"}. Priority context for LLMs.
      </div>
    </section>
  );
};
